/**
 * @file select-ticket.component.ts
 * @description This file defines the `SelectTicketComponent`, which is responsible for managing the ticket selection process in the ticket booking feature of the application. It handles ticket selection, incrementing/decrementing ticket counts, calculating totals, and interacting with the backend services for ticket registration.
 *
 * @module SelectTicketComponent
 * @requires @angular/core
 * @requires @angular/common
 * @requires @angular/forms
 * @requires @angular/router
 * @requires ../ticket-booking-bll.service
 * @requires ../ticket-booking.model
 */

import { Component, ElementRef, OnInit, ViewChild } from "@angular/core";
import { HeaderComponent } from "../../../shared/layout/app/header/header.component";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { TicketBookingBllService } from "../ticket-booking-bll.service";
import {
  loyaltyApplicableModel,
  ticketColorsModal,
  registerTypeModel,
  ticketsImagesBeforeSelecteingc,
  registerTypeReqBodyModel,
} from "../ticket-booking.model";
import { ActivatedRoute, Router } from "@angular/router";
import { LeftSideNavComponent } from "../../../shared/layout/app/left-menu/left-side-nav.component";
import { LoaderService } from "../../../shared/components/loader/loader.service";
import { MatSnackBar } from "@angular/material/snack-bar";

import { UserEventStatusRespModel } from "../../profile/profile.model";
import { DeviceTypeService } from "../../../core/services/device-type.service";
import { ProfileBllService } from "../../profile/profile-bll.service";
import { UserService } from "../../../core/services/user.service";
import {
  APIErrorModel,
  BaseResponse,
} from "../../../shared/models/base-response.model";
import { EventMyTickets } from "../../tickets/tickets.model";
import { LoaderComponent } from "../../../shared/components/loader/loader.component";
import { ExhInviteBllService } from "../../exh-invite/exh-invite-bll.service";
import { InviteDetailsModel } from "../../exh-invite/exh-invite.model";
import { HomeBllService } from "../../home/home-bll.service";
import { EventDetailsResponseModel } from "../../home/home.model";

declare var bootstrap: any;

@Component({
  selector: "app-select-ticket",
  imports: [
    HeaderComponent,
    CommonModule,
    FormsModule,
    LeftSideNavComponent,
    LoaderComponent,
  ],
  templateUrl: "./select-ticket.component.html",
  styleUrl: "./select-ticket.component.scss",
})
export class SelectTicketComponent implements OnInit {
  /**
   * @property {ElementRef} loyaltyModal - Reference to the loyalty modal element.
   * @property {ElementRef} loyaltyDiscountModal - Reference to the loyalty discount modal element.
   * @property {Object} ticketsImagesBeforeSelecteingc - Stores ticket images before selection.
   * @property {Object} ticketColorsModal - Stores ticket color configurations.
   * @property {Array<registerTypeModel>} ticketDetails - List of ticket details.
   * @property {boolean} submitBtnDisabled - Indicates whether the submit button is disabled.
   * @property {Array<loyaltyApplicableModel>} loyaltyDetails - List of loyalty details.
   * @property {number} total - Total cost of selected tickets.
   */
  @ViewChild("loyaltyModal") loyaltyModal!: ElementRef;
  @ViewChild("loyaltyDiscountModal") loyaltyDiscountModal!: ElementRef;
  ticketsImagesBeforeSelecteingc: {
    [key: string]: { black: string; color: string };
  } = ticketsImagesBeforeSelecteingc;
  ticketColorsModal: {
    [key: string]: { primary: string; secondary: string };
  } = ticketColorsModal;
  ticketDetails: Array<registerTypeModel> = [];
  submitBtnDisabled: boolean = true;
  loyaltyDetails: loyaltyApplicableModel[] = [
    {
      code: "",
      discount_amount: "",
      display_name: "",
      id: "",
    },
  ];
  total: number = 0;

  /**
   * User profile data
   */
  userProfile: UserEventStatusRespModel["user_profile"] | null = null;

  isMobileDevice: boolean = false;

  previewLogo: string = "/assets/images/Default-profile-photo.png"; // String path/URL

  /**
   * The number of tickets that have not been assigned to any user.
   * Used to track and display unassigned tickets in the ticket list.
   */
  unassignedTickets: number = 0;

  /**
   * The number of tickets currently assigned to the user.
   * Used to display or track tickets that require the user's attention.
   */
  assignedTickets: number = 0;

  isLoading :boolean = true;

  // variable to hold the source of user(android_web/ios_web/web)
  source: string = "";

  inviteList: InviteDetailsModel[] = [];
  summaryId: string | null = null;

  @ViewChild('invitationModal', { static: true }) invitationModal!: ElementRef;


  eventDetails: EventDetailsResponseModel | null = null;
  
  /**
   * @constructor
   * @param {TicketBookingBllService} bllService - Service for business logic related to ticket booking.
   * @param {Router} router - Angular Router for navigation.
   */
  constructor(
    private bllService: TicketBookingBllService,
    private router: Router,
    private loaderService: LoaderService,
    private snackBar: MatSnackBar,
    private deviceService: DeviceTypeService,
    private profileBll: ProfileBllService,
    private userService: UserService,
    private activatedRoute: ActivatedRoute,
    private exhInviteBll: ExhInviteBllService,
    private homeBllService: HomeBllService
  ) {}

  /**
   * @method ngOnInit
   * @description Lifecycle hook that initializes the component by fetching register types.
   */
  ngOnInit(): void {
    this.isLoading = true;
    this.loaderService.show();
    this.isMobileDevice = this.deviceService.isMobile();
    
    // Get the source of user
    this.source = this.deviceService.getUserSource();

    Promise.all([
      this.getRegisterType(), // First API
      this.fetchUserEventStatus(), // Second API
    ])
      .then(() => {
        console.log(" Both API calls completed successfully");
      })
      .catch((error) => {
        console.error(" One or both API calls failed:", error);
      })
      .finally(() => {
        this.isLoading = false;
        this.loaderService.hide();
      });

      const refSource = this.getRefSourceFromRoute();
      if (refSource) {
        this.bllService.saveRefSource(refSource);
      }

      this.getPendingInvites();

      this.loadEventDetails();

      
  }

  /**
   * @method goBack
   * @description Navigates back to the home page.
   */
  goBack(): void {
    this.router.navigate(["home"]);
  }

  /**
   * @method getRegisterType
   * @description Fetches the register types from the backend and updates ticket details.
   */
  getRegisterType(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.bllService
        .getEventRegisterType()
        .then((result) => {
          this.handleTicketDetailsChange(result);
          resolve();
        })
        .catch((error: APIErrorModel) => {
          console.error("Error fetching register types:", error);
          if(error.status === 410){
           this.router.navigate(['/home'],{replaceUrl: true});
          }
          
        });
    });
  }

  /**
   * @method handleTicketDetailsChange
   * @description Updates ticket details and calculates the total cost.
   * @param {any} result - The result from the backend containing ticket details.
   */
  handleTicketDetailsChange(result: any): void {
    const registerTypes = this.bllService.extractRegisterTypes(result);
    const userDiscounts = this.bllService.extractUserDiscounts(result);

    this.ticketDetails = this.bllService.formatTickets(registerTypes);
    this.loyaltyDetails = userDiscounts;

    console.log("Ticket details fetched successfully:", this.ticketDetails);

    this.summaryId = this.bllService.getSummaryIdFromStorage();
    if(this.summaryId){
      this.getRegistrationDetails(this.summaryId).then((details) => {
        console.log(details);
        if (details) {
          this.ticketDetails = this.bllService.mergeStoredTickets(
            this.ticketDetails,
            details
          );
        } else {
          console.warn("No registration details found.");
        }
        this.calculateTotal();
      });
    }
   
  }

  /**
   * @method handleError
   * @description Logs an error message when fetching ticket details fails.
   * @param {any} error - The error object.
   */
  handleError(error: any): void {
    console.error("An error occurred while fetching ticket details:", error);
  }
  fetchUserEventStatus(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.profileBll
        .submitGetUserEventStatus()
        .then((res: BaseResponse<UserEventStatusRespModel>) => {
          this.userProfile = res?.data?.user_profile;

          if (this.userProfile) {
            const previewLogo =
              this.userProfile.image_url ||
              "/assets/images/Default-profile-photo.png";
            const userName = `${this.userProfile.first_name ?? ""} ${
              this.userProfile.last_name ?? ""
            }`.trim();

            this.userService.setUser({
              name: userName,
              profileImg: previewLogo,
              mobile: this.userProfile.mobile,
              state: this.userProfile.state,
              city: this.userProfile.city,
              hasUnassignedTickets: res.data.event_my_tickets.unassigned_tickets_bought_by_me > 0,
              hasAssignedTickets: res.data.event_my_tickets.assigned_tickets_bought_by_me > 0 || res.data.event_my_tickets.tickets_assigned_for_me > 0,
            });

            if (res?.data?.event_my_tickets) {
              this.getEventTickets(res.data.event_my_tickets);
            }
          }
          resolve();
        })
        .catch((err: APIErrorModel) => {
          console.error("Error fetching user event status:", err);
          reject(err);
        });
    });
  }

  /**
   * Updates the ticket lists for the current event.
   *
   * Assigns the unassigned and assigned tickets from the provided `EventMyTickets` object
   * to the component's respective properties.
   *
   * @param eventMyTickets - An object containing the user's tickets for the event,
   * including unassigned tickets bought by the user and tickets assigned to the user.
   */
  getEventTickets(eventMyTickets: EventMyTickets): void {
    this.unassignedTickets = eventMyTickets.unassigned_tickets_bought_by_me;
    this.assignedTickets = eventMyTickets.tickets_assigned_for_me;
    const bought = eventMyTickets.tickets_bought_by_me;
    const assigned = eventMyTickets.assigned_tickets_bought_by_me;
    // condition: user bought tickets but has not assigned any
    // if (bought > 0 && assigned === 0) {
    //   this.router.navigate(["/reg/assign-ticket"], { replaceUrl: true });
    // }
  }

  /**
   * @method trackByIndex
   * @description Tracks items in a list by their index.
   * @param {number} index - The index of the item.
   * @returns {number} The index of the item.
   */
  trackByIndex(index: number): number {
    return index;
  }

  /**
   * @method findTicketById
   * @description Finds a ticket by its ID.
   * @param {number} id - The ID of the ticket.
   * @returns {any} The ticket object.
   */
  private findTicketById(id: number): any {
    return this.ticketDetails.find((t) => t.id === id);
  }

  /**
   * @method Onincreament
   * @description Increments the count of a ticket.
   * @param {number} id - The ID of the ticket.
   */
  Onincreament(id: number): void {
    const ticket = this.findTicketById(id);
    if (ticket) {
      this.bllService.incrementTicketCount(ticket);
      this.calculateTotal();
    }
  }

  /**
   * @method Ondecreament
   * @description Decrements the count of a ticket.
   * @param {number} id - The ID of the ticket.
   */
  Ondecreament(id: number): void {
    const ticket = this.findTicketById(id);
    if (ticket) {
      this.bllService.decrementTicketCount(ticket);
      this.calculateTotal();
    }
  }

  /**
   * @method selectTicket
   * @description Toggles the selection of a ticket.
   * @param {number} ticketId - The ID of the ticket.
   * @param {boolean} isSelected - Whether the ticket is selected.
   */
  selectTicket(event: MouseEvent, ticketId: number, isSelected: boolean): void {
    const target = event.target as HTMLElement;

    // Check if the click happened inside the input or buttons
    const isInputOrButton = target.closest(".count") !== null;

    if (isInputOrButton) {
      // Don't toggle selection if user clicked inside count controls
      return;
    }
    const ticket = this.findTicketById(ticketId);
    if (ticket) {
      ticket.isSelected = isSelected ? false : true;
      ticket.count = ticket.isSelected ? 1 : 0;
      this.calculateTotal();
    }
  }

  /**
   * @method calculateTotal
   * @description Calculates the total cost of selected tickets and updates the submit button state.
   */
  calculateTotal(): void {
    this.total = this.bllService.calculateTotal(this.ticketDetails);
    this.submitBtnDisabled = this.total === 0;
  }

  /**
   * @method showLoyaltyModal
   * @description Displays the loyalty modal.
   */
  showLoyaltyModal(): void {
    this.loyaltyModal.nativeElement.style.display = "block";
    const modal = new bootstrap.Modal(this.loyaltyModal.nativeElement);
    modal.show();
  }

  /**
   * @method onChangeCount
   * @description Handles changes to the ticket count input.
   * @param {registerTypeModel} ticket - The ticket model being modified.
   */
  onChangeCount(ticket: registerTypeModel): void {
    // Convert count to number first (or 0 if invalid)
    const parsedCount = parseInt(ticket.count?.toString() || "", 10);

    // If parsedCount is not a number or 0, reset to 0 and deselect
    if (isNaN(parsedCount) || parsedCount <= 0) {
      ticket.count = 0;
      ticket.isSelected = false;
    } else {
      ticket.count = parsedCount;

      // Force selection if valid count entered manually
      if (!ticket.isSelected) {
        ticket.isSelected = true;
      }
    }

    this.calculateTotal();
  }

  /**
   * @method onBlurCount
   * @description Handles the blur event of the ticket count input, resetting to 0 if empty.
   * @param {registerTypeModel} ticket - The ticket model being modified.
   */
  onBlurCount(ticket: registerTypeModel, inputRef: HTMLInputElement): void {
    const rawValue = inputRef.value.trim();

    // If input is empty or not a number
    if (rawValue === "" || isNaN(Number(rawValue))) {
      ticket.count = 0;
      ticket.isSelected = false;

      // Force update input field visually
      inputRef.value = "0";

      // Optional: trigger change detection or any other logic
      this.calculateTotal();
    }
  }

  /**
   * @method allowOnlyNumbers
   * @description Allows only numeric input in the ticket count input field.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  allowOnlyNumbers(event: KeyboardEvent): void {
    const charCode = event.key;
    // Allow control keys (backspace, delete, arrows, etc.)
    const allowedKeys = [
      "Backspace",
      "Delete",
      "ArrowLeft",
      "ArrowRight",
      "ArrowUp",
      "ArrowDown",
      "Tab",
      "Home",
      "End",
      "Enter",
      "Escape",
    ];
    if (!/^\d$/.test(charCode) && !allowedKeys.includes(charCode)) {
      event.preventDefault(); // Block anything that's not a digit
    }
  }

  /**
   * @method preventNonNumericPaste
   * @description Prevents pasting non-numeric content into the ticket count input.
   * @param {ClipboardEvent} event - The paste event.
   */
  preventNonNumericPaste(event: ClipboardEvent): void {
    const pastedInput: string = event.clipboardData?.getData("text") || "";

    if (!/^\d*$/.test(pastedInput)) {
      event.preventDefault(); // Block if pasted value is not all digits
    }
  }

  /**
   * @method closemodal
   * @description Hides the loyalty modal.
   */
  closemodal(): void {
    this.loyaltyModal.nativeElement.style.display = "none";
    const modal = bootstrap.Modal.getInstance(this.loyaltyModal.nativeElement);
    modal.hide();
  }

  /**
   * @method addTickets
   * @description Displays the loyalty discount modal.
   */
  addTickets(): void {
    this.loyaltyDiscountModal.nativeElement.style.display = "block";
    const modal = new bootstrap.Modal(this.loyaltyDiscountModal.nativeElement);
    modal.show();
  }

  /**
   * @method closeLoyaltyModal
   * @description Hides the loyalty discount modal and submits selected tickets to the backend.
   */
  closeLoyaltyModal(): void {
    this.loyaltyDiscountModal.nativeElement.style.display = "none";
    const modal = bootstrap.Modal.getInstance(
      this.loyaltyDiscountModal.nativeElement
    );
    if (modal) {
      modal.hide();
    }

    this.submitRegisterTypes();
  }

  /**
   * @method buildTicketRequest
   * @description Constructs the request body for adding tickets.
   * @returns {registerTypeReqBodyModel | null} The request body or null if no tickets are selected.
   */
  private buildTicketRequest(): registerTypeReqBodyModel | null {
    const selectedTickets = this.ticketDetails.filter(
      (ticket) => ticket.isSelected && ticket.count > 0
    );

    if (selectedTickets.length === 0) {
      console.error("No tickets selected.");
      alert("Please select at least one ticket.");
      return null;
    }

    const refSource = this.bllService.getRefSource();

    return {
      language: this.profileBll.getSelectedLanguage(),
      source: this.source,
      select_reg_types: selectedTickets.map((ticket: any) => ({
        ticket_code: ticket.code,
        quantity: ticket.count,
      })),
       ...(refSource && refSource.trim() ? { ref_source: refSource } : {})
    };
  }

  /**
   * @method getSelectedTickets
   * @description Retrieves the selected tickets from the ticket details.
   * @returns {any[]} An array of selected tickets.
   */
  private getSelectedTickets(): any[] {
    return this.ticketDetails.filter(
      (ticket) => ticket.isSelected && ticket.count > 0
    );
  }

  /**
   * @method isOnlyChildTicketSelected
   * @description Checks if only the child ticket is selected.
   * @returns {boolean} True if only the child ticket is selected, false otherwise.
   */
  isOnlyChildTicketSelected(): boolean {
    // Check if only child tickets are selected
    const selectedRegisterTypes = this.getSelectedTickets();
    return (
      selectedRegisterTypes.length === 1 &&
      selectedRegisterTypes[0].code === "child_single_entry"
    );
  }

  /**
   * @method onClickOfSubmit
   * @description Handles the click event of the submit button. If a summary ID exists, it updates the registration details; otherwise, it processes to add register types or adds tickets.
   * @returns {void}
   */
  onClickOfSubmit(): void {
    if (this.loyaltyDetails.length === 0) {
      this.submitRegisterTypes();
    } else {
      this.addTickets();
    }
  }

  showError() {
    this.snackBar.open(
      "Child ticket cannot be purchased alone. Kindly add another ticket to continue.",
      "Close",
      {
        duration: 3000, // Duration in milliseconds
        verticalPosition: "bottom", // Position of the snackbar
      }
    );
  }

  /**
   * @method getRegistrationDetails
   * @description Fetches registration details from the backend.
   * @returns {Promise<any>} A promise resolving to the registration details.
   */
  getRegistrationDetails(summaryId: string): Promise<any> {
    this.loaderService.show();
    return this.bllService
      .getRegistrationDetails(summaryId)
      .then((result) => {
        this.loaderService.hide();
        return result.ticket_type_details;
      })
      .catch((error) => {
        console.error(
          "An error occurred while fetching registration details:",
          error
        );
        this.loaderService.hide();
        return null;
      });
  }

  /**
   * @method submitRegisterTypes
   * @description Submits the selected register types to the backend and updates the ticket details.
   * @returns {void}
   */

  submitRegisterTypes(): void {
    const requestBody = this.buildTicketRequest();
    if (!requestBody) return;

    const selectedTickets = this.getSelectedTickets();
    this.loaderService.show();
    this.bllService
      .SubmitRegisterTypes(requestBody)
      .then((result) => {
        console.log("Tickets added successfully:", result);
        this.bllService.handleTicketDetailsChange(selectedTickets, result);
        // this.loaderService.hide();
      })
      .catch((error: { status: number; message: string }) => {
        console.error("An error occurred while adding tickets:", error);
        if (error.status === 422) {
          this.snackBar.open(
            "Child ticket cannot be selected without an adult ticket",
            "Close",
            {
              duration: 3000, // Duration in milliseconds
              verticalPosition: "bottom", // Position of the snackbar
            }
          );
        }
        // alert(`Error ${error.status}: ${error.message}`);
        this.loaderService.hide();
      });
  }

  /**
   * Navigates the user to the ticket assignment page.
   * This method uses the Angular Router to redirect to the '/reg/assign-ticket' route.
   */
  assignTickets(): void {
    this.router.navigate(["/reg/assign-ticket"]);
  }

  private getRefSourceFromRoute(): string {
    return this.activatedRoute.snapshot.queryParamMap.get("ref_source") ?? "";
  }
  getDiscountPercent(ticket: registerTypeModel): number {
    const original = Number(ticket.display_price);
    const paid = Number(ticket.paid_amount);
  
    if (!original || original <= 0) return 0;
  
    const discount = ((original - paid) / original) * 100;
  
    return discount >= 0 ? discount : 0;
  }
  
  
  getPendingInvites() {
    this.exhInviteBll
      .getPendingInviteList()
      .then((response) => {
        // Validate response data structure
        this.inviteList = Array.isArray(response?.data) ? response.data : [];
        if (this.inviteList.length > 0){
          const modalEl = this.invitationModal.nativeElement;
          const modal = this.homeBllService.getBootstrapModal(modalEl);
          modal.show();
        }
      })
      .catch((error) => {
        console.error("Error fetching pending invites:", error);
        // keep state consistent on error
        this.inviteList = [];
      });
  }

   /**
   * Navigates the user to the accept invitation page.
   */
   navigateToAcceptInvite(): void {
    const modalEl = this.invitationModal.nativeElement;
    const bsModal = this.homeBllService.getBootstrapModal(modalEl);
    
    bsModal.hide();
    // guard: no invites loaded or list is empty
    if (!this.inviteList || this.inviteList.length === 0) {
      return;
    }
    if (this.inviteList.length === 1) {
      // navigate to invite detail page
      const code = this.inviteList[0]?.encoded_invite_code;
      if (!code) {
        return;
      }
      this.router.navigate(["/exh-invite/details"], {
        queryParams: { code },
      });
    } else {
      this.router.navigate(["/exh-invite/list"]);
    }
  }

  loadEventDetails() {
    this.homeBllService.fetchEventDetails()
      .then((response) => {
        this.eventDetails = response.data;     
        this.userService.setLeftMenu({
          allow_to_register: this.eventDetails.allow_to_register,
          show_digital_catalog: this.eventDetails.show_digital_catalog,
          event_status: this.eventDetails.event_status
        });       
      })
      .catch((error) => {
        console.error('Error fetching event details:', error);
      });
  }

}
